import React, { useState } from "react";
import { useForm } from "react-hook-form";
import emailjs from '@emailjs/browser';

const ContactForm = () => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | null

    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
    } = useForm({
        mode: "onBlur" // Validates when the user leaves an input
    });

    const onSubmit = async (data) => {
        setIsSubmitting(true);
        setSubmitStatus(null);
        const SERVICE_ID = "service_r3prdtz";
        const PUBLIC_KEY = "DWejhSamduD0CYuKD";

        const ADMIN_TEMPLATE_ID = "template_84j69im";
        const CLIENT_AUTO_REPLY_ID = "template_ujaeta5";

        try {
            // 1. Send Notification to Admin (Rahul)
            const adminPromise = emailjs.send(
                SERVICE_ID,
                ADMIN_TEMPLATE_ID,
                {
                    from_name: data.name,
                    from_email: data.email,
                    message: data.message,
                },
                PUBLIC_KEY
            );

            // 2. Send Professional Auto-Reply to Client
            const clientPromise = emailjs.send(
                SERVICE_ID,
                CLIENT_AUTO_REPLY_ID,
                {
                    from_name: data.name, // Used for "Dear {{from_name}}"
                    reply_to: data.email, // Ensures the mail goes to the sender
                },
                PUBLIC_KEY
            );

            // Execute both calls
            const [adminRes, clientRes] = await Promise.all([adminPromise, clientPromise]);

            if (adminRes.status === 200) {
                setSubmitStatus("success");
                reset();
            }
        } catch (error) {
            console.error("EmailJS Error:", error);
            setSubmitStatus("error");
        } finally {
            setIsSubmitting(false);
            setTimeout(() => setSubmitStatus(null), 5000);
        }
    };

    return (
        <div className="relative bg-white/5 backdrop-blur-md rounded-[2rem] p-8 md:p-12 border border-white/10 shadow-2xl">
            {/* Feedback Messages */}
            {submitStatus === "success" && (
                <div className="mb-6 p-4 rounded-2xl bg-green-500/10 border border-green-500/20 text-green-400 text-sm text-center animate-in fade-in zoom-in duration-300">
                    Message sent successfully! I'll get back to you soon.
                </div>
            )}
            {submitStatus === "error" && (
                <div className="mb-6 p-4 rounded-2xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm text-center">
                    Something went wrong. Please try again later.
                </div>
            )}

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    {/* Name Field */}
                    <div className="space-y-2">
                        <label className="text-[10px] font-black uppercase tracking-widest text-gray-500 ml-1">
                            Name
                        </label>
                        <input
                            {...register("name", {
                                required: "Name is required",
                                minLength: { value: 2, message: "Name is too short" }
                            })}
                            className={`w-full bg-white/5 border ${errors.name ? 'border-red-500/50' : 'border-white/10'} rounded-2xl px-5 py-4 text-white focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all placeholder:text-white/20`}
                            placeholder="Your Name"
                            type="text"
                        />
                        {errors.name && (
                            <p className="text-red-400 text-[9px] font-bold uppercase tracking-wider ml-1 animate-in slide-in-from-top-1">
                                {errors.name.message}
                            </p>
                        )}
                    </div>

                    {/* Email Field */}
                    <div className="space-y-2">
                        <label className="text-[10px] font-black uppercase tracking-widest text-gray-500 ml-1">
                            Email
                        </label>
                        <input
                            {...register("email", {
                                required: "Email is required",
                                pattern: {
                                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                    message: "Invalid email address"
                                }
                            })}
                            className={`w-full bg-white/5 border ${errors.email ? 'border-red-500/50' : 'border-white/10'} rounded-2xl px-5 py-4 text-white focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all placeholder:text-white/20`}
                            placeholder="hello@example.com"
                            type="email"
                        />
                        {errors.email && (
                            <p className="text-red-400 text-[9px] font-bold uppercase tracking-wider ml-1 animate-in slide-in-from-top-1">
                                {errors.email.message}
                            </p>
                        )}
                    </div>
                </div>

                {/* Message Field */}
                <div className="space-y-2">
                    <label className="text-[10px] font-black uppercase tracking-widest text-gray-500 ml-1">
                        Message
                    </label>
                    <textarea
                        {...register("message", {
                            required: "Please enter your message",
                            minLength: { value: 10, message: "Tell me a bit more (min 10 characters)" }
                        })}
                        className={`w-full bg-white/5 border ${errors.message ? 'border-red-500/50' : 'border-white/10'} rounded-2xl px-5 py-4 text-white focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all resize-none placeholder:text-white/20`}
                        placeholder="Tell me about your vision..."
                        rows="4"
                    ></textarea>
                    {errors.message && (
                        <p className="text-red-400 text-[9px] font-bold uppercase tracking-wider ml-1 animate-in slide-in-from-top-1">
                            {errors.message.message}
                        </p>
                    )}
                </div>

                {/* Submit Button */}
                <button
                    disabled={isSubmitting}
                    className="group w-full bg-primary hover:bg-primary-dark text-white font-bold py-5 rounded-2xl transition-all shadow-glow shadow-primary/20 mt-4 font-display flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
                    type="submit"
                >
                    {isSubmitting ? (
                        <div className="flex items-center gap-2">
                            <span className="animate-spin size-4 border-2 border-white/20 border-t-white rounded-full"></span>
                            <span>Sending...</span>
                        </div>
                    ) : (
                        <>
                            <span>Send Message</span>
                            <span className="material-symbols-outlined text-sm group-hover:translate-x-1 transition-transform">
                                send
                            </span>
                        </>
                    )}
                </button>
            </form>
        </div>
    );
};

export default ContactForm;